import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CreditCard as CCIcon, Plus, Pencil, Trash, Warning, CheckCircle, ArrowsClockwise, Repeat } from "@phosphor-icons/react";
import { PageHeader } from "../components/ui/kaizen";

const STATUS_COLORS = {
  healthy: "text-[#0A8754] bg-green-50",
  warning: "text-amber-700 bg-amber-50",
  critical: "text-[#D90429] bg-red-50",
};

export default function CreditMonitor() {
  const [data, setData] = useState(null);
  const [recurring, setRecurring] = useState({ recurring: [], monthly_estimate_total: 0 });
  const [editing, setEditing] = useState(null); // null | 'new' | card
  const [tab, setTab] = useState("cards");
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [d, r] = await Promise.all([
        api.get("/credit/dashboard"),
        api.get("/credit/recurring"),
      ]);
      setData(d.data);
      setRecurring(r.data);
    } catch {
      toast.error("Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const saveCard = async (card) => {
    try {
      if (card.id) await api.patch(`/credit/cards/${card.id}`, card);
      else await api.post("/credit/cards", card);
      toast.success("Sauvegardé");
      setEditing(null);
      reload();
    } catch {
      toast.error("Erreur");
    }
  };

  const deleteCard = async (id) => {
    if (!window.confirm("Supprimer cette carte ?")) return;
    try {
      await api.delete(`/credit/cards/${id}`);
      reload();
    } catch {
      toast.error("Erreur");
    }
  };

  if (loading) return <div className="p-8 text-center">Chargement…</div>;
  const summary = data?.summary || {};
  const cards = data?.cards || [];
  const alerts = data?.alerts || [];

  return (
    <div className="space-y-6 p-6 md:p-8" data-testid="credit-monitor-page">
      <PageHeader
        title="Crédit & Paiements Récurrents"
        breadcrumbs={[{ label: "Finance & administration" }, { label: "Crédit" }]}
        subtitle="Objectif : rester sous 70% d'utilisation par carte."
        actions={
          <>
            <button onClick={reload} className="px-4 py-2.5 rounded-lg border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-100 flex items-center gap-2 text-sm" data-testid="refresh-btn">
              <ArrowsClockwise size={14} /> Actualiser
            </button>
            <button onClick={() => setEditing("new")} className="bg-[#D90429] text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#BF0424] flex items-center gap-2" data-testid="new-card-btn">
              <Plus size={16} /> Nouvelle carte
            </button>
          </>
        }
      />

      {/* SUMMARY */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Limite totale" value={`${summary.total_limit?.toFixed(2) || 0} $`} />
        <Stat label="Solde total" value={`${summary.total_balance?.toFixed(2) || 0} $`} highlight={summary.overall_status} />
        <Stat label="Crédit dispo" value={`${summary.total_available?.toFixed(2) || 0} $`} />
        <Stat label="Util. globale" value={`${summary.overall_utilization || 0}%`} highlight={summary.overall_status} />
      </div>

      {/* ALERTS */}
      {alerts.length > 0 && (
        <div className="hard-border bg-amber-50 p-4 space-y-2">
          <div className="flex items-center gap-2 font-bold"><Warning size={18} weight="fill" className="text-amber-700" /> Alertes</div>
          {alerts.map((a, i) => (
            <div key={i} className={`text-sm ${a.type === "critical" ? "text-[#D90429]" : "text-amber-800"}`}>{a.message}</div>
          ))}
        </div>
      )}

      <div className="flex gap-2 hard-border-b">
        <TabBtn active={tab === "cards"} onClick={() => setTab("cards")}>Cartes ({cards.length})</TabBtn>
        <TabBtn active={tab === "recurring"} onClick={() => setTab("recurring")}>Paiements récurrents ({recurring.recurring?.length || 0})</TabBtn>
      </div>

      {tab === "cards" ? (
        <div className="space-y-3">
          {cards.length === 0 ? (
            <div className="hard-border bg-muted/20 p-12 text-center">
              <CCIcon size={48} className="mx-auto text-muted-foreground mb-3" />
              <div className="font-bold text-lg">Aucune carte enregistrée</div>
              <div className="text-sm text-muted-foreground">Ajoute tes cartes pour suivre leur utilisation et recevoir des alertes.</div>
            </div>
          ) : cards.map(c => (
            <div key={c.id} className={`hard-border bg-white p-5 ${c.status === "critical" ? "border-[#D90429]" : c.status === "warning" ? "border-amber-500" : ""}`} data-testid={`card-${c.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-display font-black text-lg">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.institution} {c.last_4 && `· **** ${c.last_4}`}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing(c)} className="hard-border p-2 hover:bg-muted" data-testid={`edit-${c.id}`}><Pencil size={14} /></button>
                  <button onClick={() => deleteCard(c.id)} className="hard-border p-2 hover:bg-red-50 text-[#D90429]" data-testid={`del-${c.id}`}><Trash size={14} /></button>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Solde</span>
                  <span className="font-bold tabular-nums">{c.current_balance?.toFixed(2)} $ / {c.credit_limit?.toFixed(2)} $</span>
                </div>
                <div className="hard-border h-3 bg-muted overflow-hidden">
                  <div className={`h-full transition-all ${c.utilization_pct >= 90 ? "bg-[#D90429]" : c.utilization_pct >= 70 ? "bg-amber-500" : "bg-[#0A8754]"}`} style={{ width: `${Math.min(c.utilization_pct, 100)}%` }} />
                </div>
                <div className="flex justify-between text-xs">
                  <span className={`ticker font-bold ${STATUS_COLORS[c.status]} px-2 py-0.5`}>{c.utilization_pct}% — {c.status === "healthy" ? "OK" : c.status === "warning" ? "Au-dessus 70%" : "CRITIQUE"}</span>
                  {c.amount_to_pay_target > 0 && <span className="text-muted-foreground">Pour revenir à 70% : <strong>{c.amount_to_pay_target.toFixed(2)} $</strong></span>}
                </div>
                {c.minimum_payment && <div className="text-xs text-muted-foreground">Paiement min : {c.minimum_payment.toFixed(2)} $ {c.due_date && `(dû ${c.due_date})`}</div>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="hard-border bg-foreground text-white p-5">
            <div className="flex items-center gap-2 ticker text-white/60"><Repeat size={14} /> ESTIMATION MENSUELLE</div>
            <div className="font-display font-black text-4xl mt-1 tabular-nums">{recurring.monthly_estimate_total?.toFixed(2) || 0} $</div>
            <div className="text-xs text-white/60 mt-1">Total des charges récurrentes mensuelles détectées</div>
          </div>
          <div className="hard-border bg-white">
            <table className="w-full text-sm" data-testid="recurring-table">
              <thead className="hard-border-b bg-muted/30 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-left">Catégorie</th>
                  <th className="px-3 py-2 text-right">Montant moyen</th>
                  <th className="px-3 py-2 text-center">Cadence</th>
                  <th className="px-3 py-2 text-center">Compte</th>
                  <th className="px-3 py-2 text-center">Occurrences</th>
                </tr>
              </thead>
              <tbody>
                {(recurring.recurring || []).slice(0, 80).map((r, i) => (
                  <tr key={i} className="hard-border-b">
                    <td className="px-3 py-2">{r.description_sample}</td>
                    <td className="px-3 py-2"><span className="hard-border px-2 py-0.5 text-xs bg-muted/30">{r.category}</span></td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold">{r.avg_amount.toFixed(2)} $</td>
                    <td className="px-3 py-2 text-center text-xs">{r.cadence}</td>
                    <td className="px-3 py-2 text-center text-xs">{(r.accounts || []).join(", ")}</td>
                    <td className="px-3 py-2 text-center tabular-nums">{r.count}× ({r.months}mo)</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && <CardModal card={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSave={saveCard} />}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  const cls = highlight === "critical" ? "text-[#D90429]" : highlight === "warning" ? "text-amber-700" : "";
  return (
    <div className="hard-border bg-white p-4">
      <div className="ticker text-xs text-muted-foreground">{label}</div>
      <div className={`font-display font-black text-2xl tabular-nums mt-1 ${cls}`}>{value}</div>
    </div>
  );
}
function TabBtn({ active, onClick, children }) {
  return <button onClick={onClick} className={`px-4 py-2 ticker ${active ? "bg-foreground text-white" : "hover:bg-muted"}`}>{children}</button>;
}

function CardModal({ card, onClose, onSave }) {
  const [form, setForm] = useState({
    name: card?.name || "",
    institution: card?.institution || "",
    last_4: card?.last_4 || "",
    credit_limit: card?.credit_limit ?? 0,
    current_balance: card?.current_balance ?? 0,
    minimum_payment: card?.minimum_payment ?? "",
    interest_rate_annual: card?.interest_rate_annual ?? "",
    due_date: card?.due_date || "",
  });
  const save = () => {
    if (!form.name) return toast.error("Nom requis");
    if (!form.credit_limit || form.credit_limit <= 0) return toast.error("Limite invalide");
    const data = {
      ...form,
      credit_limit: parseFloat(form.credit_limit),
      current_balance: parseFloat(form.current_balance || 0),
      minimum_payment: form.minimum_payment ? parseFloat(form.minimum_payment) : null,
      interest_rate_annual: form.interest_rate_annual ? parseFloat(form.interest_rate_annual) : null,
      ...(card ? { id: card.id } : {}),
    };
    onSave(data);
  };
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white hard-border w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="card-modal">
        <div className="hard-border-b p-4 flex justify-between font-display font-black text-xl">
          {card ? "Éditer carte" : "Nouvelle carte"}
          <button onClick={onClose}>×</button>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Nom (ex: Visa Affaires)" value={form.name} onChange={v => setForm({ ...form, name: v })} testid="card-name" />
          <div className="grid grid-cols-2 gap-2">
            <Field label="Institution" value={form.institution} onChange={v => setForm({ ...form, institution: v })} testid="card-bank" />
            <Field label="4 derniers chiffres" value={form.last_4} onChange={v => setForm({ ...form, last_4: v })} testid="card-last4" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Limite ($)" type="number" value={form.credit_limit} onChange={v => setForm({ ...form, credit_limit: v })} testid="card-limit" />
            <Field label="Solde actuel ($)" type="number" value={form.current_balance} onChange={v => setForm({ ...form, current_balance: v })} testid="card-balance" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Paiement min ($)" type="number" value={form.minimum_payment} onChange={v => setForm({ ...form, minimum_payment: v })} testid="card-min" />
            <Field label="Taux annuel (%)" type="number" value={form.interest_rate_annual} onChange={v => setForm({ ...form, interest_rate_annual: v })} testid="card-rate" />
          </div>
          <Field label="Date d'échéance" type="date" value={form.due_date} onChange={v => setForm({ ...form, due_date: v })} testid="card-due" />
          <button onClick={save} className="w-full bg-[#D90429] text-white py-2 ticker hover:bg-[#BF0424]" data-testid="save-card">Sauvegarder</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="ticker text-xs text-muted-foreground">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} className="w-full hard-border px-3 py-2 mt-1" data-testid={testid} />
    </div>
  );
}
